import StateMachine from '../statemachine/StateMachine'
import { sharedInstance as events } from './EventCenter'
import ObstaclesController from './ObstaclesController'

const CAJERO_SPRITESHEET_COLUMNS = 4
const CAJERO_MOVE_FRAME_RATE = 10
const CAJERO_DEATH_FRAME_RATE = 12
const CAJERO_DEFAULT_TRAVEL_DISTANCE = 200
const CAJERO_DISAPPEAR_DELAY_MS = 1000
const CAJERO_FADE_OUT_DURATION_MS = 400
const CAJERO_NON_GROUND_DEATH_Y_OFFSET = 10
const CAJERO_SURFACE_PROBE_DEPTH = 48
const CAJERO_SURFACE_PROBE_HORIZONTAL_MARGIN = 6

type CajeroConfig = {
	startX: number
	travelDistance?: number
	moveSpeed: number
	animationKeyPrefix: string
	initialDirection?: 1 | -1
}

export default class CajeroController
{
	private scene: Phaser.Scene
	private sprite: Phaser.Physics.Matter.Sprite
	private stateMachine: StateMachine
	private moveSpeed: number
	private minX: number
	private maxX: number
	private patrolY: number
	private animationMoveRightKey: string
	private animationMoveLeftKey: string
	private animationDeathRightKey: string
	private animationDeathLeftKey: string
	private facing: 1 | -1 = 1
	private isDead = false
	private despawnStarted = false
	private initialDirection: 1 | -1
	private obstacles: ObstaclesController
	private deathVisual?: Phaser.GameObjects.Sprite
	private shouldApplyNonGroundDeathOffset = false

	constructor(scene: Phaser.Scene, sprite: Phaser.Physics.Matter.Sprite, config: CajeroConfig, obstacles: ObstaclesController)
	{
		this.scene = scene
		this.sprite = sprite
		this.obstacles = obstacles

		const travelDistance = Math.max(0, config.travelDistance ?? CAJERO_DEFAULT_TRAVEL_DISTANCE)
		const halfTravelDistance = travelDistance * 0.5
		this.minX = config.startX - halfTravelDistance
		this.maxX = config.startX + halfTravelDistance
		this.patrolY = sprite.y
		this.moveSpeed = Math.max(0.1, config.moveSpeed)
		this.animationMoveRightKey = `${config.animationKeyPrefix}-move-right`
		this.animationMoveLeftKey = `${config.animationKeyPrefix}-move-left`
		this.animationDeathRightKey = `${config.animationKeyPrefix}-death-right`
		this.animationDeathLeftKey = `${config.animationKeyPrefix}-death-left`
		this.initialDirection = config.initialDirection ?? (Math.random() < 0.5 ? -1 : 1)

		this.createAnimations(scene)

		this.stateMachine = new StateMachine(this, `${config.animationKeyPrefix}-controller`)
		this.stateMachine
			.addState('move-right', {
				onEnter: this.moveRightOnEnter,
				onUpdate: this.moveRightOnUpdate
			})
			.addState('move-left', {
				onEnter: this.moveLeftOnEnter,
				onUpdate: this.moveLeftOnUpdate
			})
			.addState('dead', {
				onEnter: this.deadOnEnter
			})
			.setState(this.initialDirection > 0 ? 'move-right' : 'move-left')

		events.on('cajero-stomped', this.handleStomped, this)

	}

	destroy()
	{
		events.off('cajero-stomped', this.handleStomped, this)
		if (this.deathVisual)
		{
			this.deathVisual.destroy()
			this.deathVisual = undefined
		}

		if (this.sprite.active)
		{
			this.sprite.destroy()
		}
	}

	update(dt: number)
	{
		this.stateMachine.update(dt)
	}

	private createAnimations(scene: Phaser.Scene)
	{
		const textureKey = this.sprite.texture.key

		const createRowFrames = (rowIndex: number, frameCount: number) => {
			const start = rowIndex * CAJERO_SPRITESHEET_COLUMNS
			return scene.anims.generateFrameNumbers(textureKey, {
				start,
				end: start + frameCount - 1
			})
		}

		if (!scene.anims.exists(this.animationMoveRightKey))
		{
			scene.anims.create({
				key: this.animationMoveRightKey,
				frames: createRowFrames(0, 4),
				frameRate: CAJERO_MOVE_FRAME_RATE,
				repeat: -1
			})
		}

		if (!scene.anims.exists(this.animationMoveLeftKey))
		{
			scene.anims.create({
				key: this.animationMoveLeftKey,
				frames: createRowFrames(1, 4),
				frameRate: CAJERO_MOVE_FRAME_RATE,
				repeat: -1
			})
		}

		if (!scene.anims.exists(this.animationDeathRightKey))
		{
			scene.anims.create({
				key: this.animationDeathRightKey,
				frames: createRowFrames(2, 4),
				frameRate: CAJERO_DEATH_FRAME_RATE,
				repeat: 0
			})
		}

		if (!scene.anims.exists(this.animationDeathLeftKey))
		{
			scene.anims.create({
				key: this.animationDeathLeftKey,
				frames: createRowFrames(3, 4),
				frameRate: CAJERO_DEATH_FRAME_RATE,
				repeat: 0
			})
		}
	}

	private moveRightOnEnter()
	{
		if (this.isDead)
		{
			return
		}

		this.facing = 1
		this.sprite.play(this.animationMoveRightKey, true)
	}

	private moveRightOnUpdate()
	{
		if (this.isDead)
		{
			return
		}

		const { rightLimit } = this.getEffectivePatrolLimits()

		this.sprite.setY(this.patrolY)
		this.sprite.setVelocityY(0)
		this.sprite.setVelocityX(this.moveSpeed)
		if (this.sprite.x >= rightLimit)
		{
			this.stateMachine.setState('move-left')
		}
	}

	private moveLeftOnEnter()
	{
		if (this.isDead)
		{
			return
		}

		this.facing = -1
		this.sprite.play(this.animationMoveLeftKey, true)
	}

	private moveLeftOnUpdate()
	{
		if (this.isDead)
		{
			return
		}

		const { leftLimit } = this.getEffectivePatrolLimits()

		this.sprite.setY(this.patrolY)
		this.sprite.setVelocityY(0)
		this.sprite.setVelocityX(-this.moveSpeed)
		if (this.sprite.x <= leftLimit)
		{
			this.stateMachine.setState('move-right')
		}
	}

	private getEffectivePatrolLimits(): { leftLimit: number, rightLimit: number }
	{
		const body = this.sprite.body as MatterJS.BodyType | undefined
		const halfBodyWidth = body
			? Math.max(1, (body.bounds.max.x - body.bounds.min.x) * 0.5)
			: Math.max(1, this.sprite.displayWidth * 0.5)

		const worldBounds = this.scene.matter.world.engine.world.bounds
		const worldLeft = Number.isFinite(worldBounds.min.x) ? worldBounds.min.x : this.minX
		const worldRight = Number.isFinite(worldBounds.max.x) ? worldBounds.max.x : this.maxX

		const leftLimit = Math.max(this.minX, worldLeft + halfBodyWidth)
		const rightLimit = Math.max(leftLimit, Math.min(this.maxX, worldRight - halfBodyWidth))

		return { leftLimit, rightLimit }
	}

	private deadOnEnter()
	{
		this.isDead = true
		this.sprite.setData('isDead', true)
		const body = this.sprite.body as MatterJS.BodyType
		const landingSurfaceBody = this.resolveLandingSurfaceBody(body)
		this.shouldApplyNonGroundDeathOffset = this.isNonGroundSurface(landingSurfaceBody)
		this.obstacles.remove('cajero', body)

		const deathVisual = this.scene.add.sprite(this.sprite.x, this.sprite.y, this.sprite.texture.key, this.sprite.frame.name)
		deathVisual.setOrigin(this.sprite.originX, this.sprite.originY)
		deathVisual.setDisplaySize(this.sprite.displayWidth, this.sprite.displayHeight)
		deathVisual.setDepth(this.sprite.depth - 1)
		deathVisual.setFlipX(this.sprite.flipX)
		this.deathVisual = deathVisual

		this.scene.matter.world.remove(body)
		this.sprite.destroy()

		if (this.facing > 0)
		{
			deathVisual.play(this.animationDeathRightKey, true)
			deathVisual.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
				this.startDespawn()
			})
			return
		}

		deathVisual.play(this.animationDeathLeftKey, true)
		deathVisual.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
			this.startDespawn()
		})
	}

	private startDespawn()
	{
		if (this.despawnStarted)
		{
			return
		}

		if (!this.deathVisual || !this.deathVisual.active)
		{
			return
		}

		this.despawnStarted = true
		this.deathVisual.anims.stop()

		const finalFrame = this.facing > 0
			? (2 * CAJERO_SPRITESHEET_COLUMNS) + 3
			: (3 * CAJERO_SPRITESHEET_COLUMNS) + 3
		this.deathVisual.setFrame(finalFrame)
		if (this.shouldApplyNonGroundDeathOffset)
		{
			this.deathVisual.y += CAJERO_NON_GROUND_DEATH_Y_OFFSET
		}

		this.scene.time.delayedCall(CAJERO_DISAPPEAR_DELAY_MS, () => {
			if (!this.deathVisual || !this.deathVisual.active)
			{
				return
			}

			this.scene.tweens.add({
				targets: this.deathVisual,
				alpha: 0,
				duration: CAJERO_FADE_OUT_DURATION_MS,
				onComplete: () => {
					if (!this.deathVisual)
					{
						return
					}

					this.deathVisual.destroy()
					this.deathVisual = undefined
				}
			})
		})
	}

	private resolveLandingSurfaceBody(ownBody: MatterJS.BodyType): MatterJS.BodyType | undefined
	{
		const worldBodies = this.scene.matter.world.engine.world.bodies as MatterJS.BodyType[]
		const ownBounds = ownBody.bounds
		const ownMinX = ownBounds.min.x - CAJERO_SURFACE_PROBE_HORIZONTAL_MARGIN
		const ownMaxX = ownBounds.max.x + CAJERO_SURFACE_PROBE_HORIZONTAL_MARGIN
		const ownBottomY = ownBounds.max.y

		const candidates = worldBodies.filter(body => {
			if (body === ownBody || !body.isStatic || body.isSensor)
			{
				return false
			}

			const surfaceBounds = body.bounds
			const hasHorizontalOverlap = surfaceBounds.max.x >= ownMinX && surfaceBounds.min.x <= ownMaxX
			if (!hasHorizontalOverlap)
			{
				return false
			}

			const isTooHighAbove = surfaceBounds.max.y < ownBottomY
			if (isTooHighAbove)
			{
				return false
			}

			const isTooFarBelow = surfaceBounds.min.y > ownBottomY + CAJERO_SURFACE_PROBE_DEPTH
			if (isTooFarBelow)
			{
				return false
			}

			return true
		})
		if (candidates.length === 0)
		{
			return undefined
		}

		candidates.sort((a, b) => {
			const aDistance = Math.abs(a.bounds.min.y - ownBottomY)
			const bDistance = Math.abs(b.bounds.min.y - ownBottomY)
			return aDistance - bDistance
		})

		return candidates[0]
	}

	private isNonGroundSurface(surfaceBody: MatterJS.BodyType | undefined): boolean
	{
		if (!surfaceBody)
		{
			return false
		}

		const hasBottomGroundOffset = (surfaceBody as MatterJS.BodyType & { hasBottomGroundOffset?: boolean }).hasBottomGroundOffset === true
		return !hasBottomGroundOffset
	}

	private handleStomped(target: Phaser.Physics.Matter.Sprite)
	{
		if (this.sprite !== target || this.isDead)
		{
			return
		}

		this.stateMachine.setState('dead')
	}
}